import type {
	// eslint-disable-next-line @typescript-eslint/no-shadow
	CSSProperties, MouseEvent as ReactMouseEvent, WheelEvent } from "react";
import { Fragment, memo, useCallback, useEffect, useState
} from "react";
import "Components/PlaybackLayout/HistoricalTimeline/HistoricalTimeline.less";
import {
	type DateString, DateUnits, ceilingDate, diffDates, diffDatesAbs, floorDate, formatDate
} from "@clintonelec/typescriptutils";
import { ZoomLevel } from "Interfaces";
import { usePropsChanged } from "@clintonelec/react-storybook";
import { events } from "Data/Dummy/Playback";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import {
	selectClipDragging, selectClipEnd, selectClipModeActive, selectClipStart, selectDate, selectScrollTimelineEnd,
	selectScrollTimelineStart, selectVideoPlayerTime, selectZoomLevel, setScrollTimelineEndAction,
	setScrollTimelineStartAction, setVideoPlayerTimeAction
} from "Data/Redux/Slices/Playback";
import ClipArea from "Components/PlaybackLayout/ClipArea";

type TimestampProps = {
	timestamp: Date;
	className?: string;
	includeSeconds?: boolean;
	scrollTimelineEnd: Date;
	scrollTimelineStart: Date;
};

const Timestamp = memo(function Timestamp(props: TimestampProps) {
	const { timestamp, className, scrollTimelineEnd, scrollTimelineStart, includeSeconds } = props;
	const format = includeSeconds ? "HH:mm:ss" : "HH:mm";
	const timestampText = formatDate(timestamp, format);
	const leftPercent = Math.max(
		0, diffDatesAbs(timestamp, scrollTimelineStart) / diffDatesAbs(scrollTimelineEnd, scrollTimelineStart) * 100
	);

	const compositeClass = [ "timestamp", className ].join(" ");
	const customStyles = {
		left: `${ leftPercent }%`
	} as CSSProperties;

	return (
		<div className={ compositeClass } style={ customStyles }>
			<span>{ timestampText }</span>
			<div className="timestamp-marker" />
		</div>
	);
});

type TimelineEventProps = {
	dayStart: Date;
	event: number;
	index: number;
	scrollTimelineEnd: Date;
	scrollTimelineStart: Date;
	zoomLevel: ZoomLevel;
};

const TimelineEvent = memo(function TimelineEvent(props: TimelineEventProps) {
	const { dayStart, event, index, scrollTimelineEnd, scrollTimelineStart, zoomLevel } = props;
	const secondsInDay = 86400;
	const eventCount = 960;
	const eventDate = new Date(dayStart.getTime() + index / eventCount * secondsInDay * DateUnits.SECONDS );
	const eventLeftPercent = diffDates(eventDate, scrollTimelineStart) /
		diffDates(scrollTimelineEnd, scrollTimelineStart) * 100;

	const customStyles = {
		width: `${ 1/eventCount * 100 * (60 / zoomLevel) }%`,
		left: `${ eventLeftPercent }%`
	} as CSSProperties;

	const eventTypes = [ "continuous-event", "alarm-event", "motion-event", "network-loss-event", "ai-event" ];
	const compositeClass = [ "event", eventTypes[ event - 1 ] ].join(" ");

	return <div className={ compositeClass } style={ customStyles } />;
});

function HistoricalTimeline() {
	const dispatch = useAppDispatch();
	const clipDragging = useAppSelector(selectClipDragging);
	const zoomLevel = useAppSelector(selectZoomLevel);
	const clipModeActive = useAppSelector(selectClipModeActive);
	const scrollTimelineStart = new Date(useAppSelector(selectScrollTimelineStart));
	const scrollTimelineEnd = new Date(useAppSelector(selectScrollTimelineEnd));
	const playheadTime = useAppSelector(selectVideoPlayerTime);
	const clipStart = useAppSelector(selectClipStart);
	const clipEnd = useAppSelector(selectClipEnd);
	const radius = 12;
	const halfTimeline = zoomLevel * radius * DateUnits.MINUTES;
	const date = useAppSelector(selectDate);
	const [ timelineDate, setTimelineDate ] = useState(new Date(playheadTime ?? 0));
	const [ scrollDistance, setScrollDistance ] = useState(0);
	const setPlayheadTime = useCallback((time: Date) => {
		dispatch(setVideoPlayerTimeAction(time.toISOString()));
	}, [ dispatch ]);

	useEffect(() => {
		const setScrollTimelineStart = (dateString: DateString) => dispatch(setScrollTimelineStartAction(dateString));
		const setScrollTimelineEnd = (dateString: DateString) => dispatch(setScrollTimelineEndAction(dateString));
		const timelineDateMilis = timelineDate?.getTime() ?? 0;

		setScrollTimelineStart(new Date(timelineDateMilis + scrollDistance - halfTimeline).toISOString());
		setScrollTimelineEnd(new Date(timelineDateMilis + scrollDistance + halfTimeline).toISOString());
	}, [ timelineDate, scrollDistance, halfTimeline, dispatch ]);

	usePropsChanged(date, () => {
		setScrollDistance(0);
		setTimelineDate(date ?? new Date(0));
	});

	usePropsChanged(zoomLevel, () => {
		if (zoomLevel === ZoomLevel.SIXTY_MINUTES) {
			setScrollDistance(0);
		}
	});

	// simulating the video player time update, this can be deleted once our video player component exists and updates
	// the videoPlayerTime
	useEffect(() => {
		const timeout = setTimeout(() => {
			if (playheadTime) {
				setPlayheadTime(new Date(new Date(playheadTime).getTime() + 1000));
			}
		}, 1000);

		return () => {
			clearTimeout(timeout);
		};
	}, [ playheadTime, setPlayheadTime ]);

	if (timelineDate?.getTime() - halfTimeline < floorDate(timelineDate)?.getTime()) {
		setTimelineDate(new Date(floorDate(timelineDate).getTime() + halfTimeline));
	} else if (timelineDate?.getTime() + halfTimeline > ceilingDate(timelineDate)?.getTime()) {
		setTimelineDate(new Date(ceilingDate(timelineDate)?.getTime() - halfTimeline));
	}

	const dayStart = floorDate(timelineDate);
	const dayEnd = ceilingDate(timelineDate);
	const [ dragging, setDragging ] = useState(false);

	const handleTimelineClick = (event: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
		if (dragging || clipModeActive) {
			return;
		}

		const { left: timelineX, width: timelineWidth } = event.currentTarget.getBoundingClientRect();
		const { clientX } = event;
		const percent = (clientX - timelineX) / timelineWidth;
		const newTimestamp = new Date(
			scrollTimelineStart?.getTime() + (radius * 2 * zoomLevel * percent * DateUnits.MINUTES)
		);

		const boundedTimestamp = newTimestamp.getTime() > new Date().getTime() ? new Date() : newTimestamp;

		setPlayheadTime(boundedTimestamp);
	};

	const calculateScrollDistance = (scrollDelta: number) => {
		const timelineEndShift = scrollTimelineEnd?.getTime() + scrollDelta;
		const timelineStartShift = scrollTimelineStart?.getTime() + scrollDelta;

		if (timelineEndShift >= dayEnd?.getTime()) {
			const maxDelta = dayEnd?.getTime() - scrollTimelineEnd?.getTime();

			return scrollDistance + maxDelta;
		} else if (timelineStartShift <= dayStart?.getTime()) {
			const minDelta = dayStart?.getTime() - scrollTimelineStart?.getTime();

			return scrollDistance + minDelta;
		} else {
			return scrollDistance + scrollDelta;
		}
	};

	const handleTimelineScroll = (event: WheelEvent) => {
		if (event.buttons & 1 || zoomLevel === ZoomLevel.SIXTY_MINUTES) {
			return;
		}

		const scrollDelta = zoomLevel * Math.sign(event.deltaY) * DateUnits.MINUTES;

		setScrollDistance(calculateScrollDistance(scrollDelta));
	};

	const handleTimelineDrag = (event: ReactMouseEvent) => {
		if (!(event.buttons & 1) || zoomLevel === ZoomLevel.SIXTY_MINUTES || clipDragging) {
			return;
		}

		setDragging(true);

		const scrollDelta = zoomLevel * event.movementX / -100 * DateUnits.MINUTES;

		setScrollDistance(calculateScrollDistance(scrollDelta));
	};

	const handleDragEnd = () => {
		setDragging(false);
	};

	const getTimestamps = () => {
		const nearestTimestamp = new Date(timelineDate?.getTime() + scrollDistance);

		nearestTimestamp.setMinutes(Math.round(nearestTimestamp.getMinutes() / zoomLevel) * zoomLevel);
		nearestTimestamp.setSeconds(0);

		const timeStamps = [ nearestTimestamp ];

		for (let i = 1; i <= radius; i++) {
			timeStamps.push(
				new Date(nearestTimestamp?.getTime() + (zoomLevel * i * DateUnits.MINUTES)),
				new Date(nearestTimestamp?.getTime() - (zoomLevel * i * DateUnits.MINUTES))
			);
		}

		return timeStamps.filter((currentTimestamp: Date) =>
			currentTimestamp > scrollTimelineStart && currentTimestamp < scrollTimelineEnd
		);
	};

	const timestampContent = getTimestamps()
		.map((currentTimestamp: Date) => (
			<Timestamp
				timestamp={ currentTimestamp }
				scrollTimelineEnd={ scrollTimelineEnd }
				scrollTimelineStart={ scrollTimelineStart }
				key={ currentTimestamp?.getTime() }
			/>
		));

	const renderPlayhead = () => {
		const playheadLeftPercent = diffDates(new Date(playheadTime ?? 0), scrollTimelineStart) /
			diffDates(scrollTimelineEnd, scrollTimelineStart) * 100;

		const customStyles = {
			left: `${ playheadLeftPercent }%`
		} as CSSProperties;

		return (
			<div className="playhead" style={ customStyles } />
		);
	};

	const renderEvents = () => {
		return events.map((event, index) => {
			return (
				<TimelineEvent
					// eslint-disable-next-line react/no-array-index-key
					key={ index }
					dayStart={ dayStart }
					event={ event }
					index={ index + 1 }
					scrollTimelineEnd={ scrollTimelineEnd }
					scrollTimelineStart={ scrollTimelineStart }
					zoomLevel={ zoomLevel }
				/>
			);
		});
	};

	if (!playheadTime) {
		return (
			<div className="timeline-container">
				<div className="timeline">
					<div className="seek-container" />
				</div>
			</div>
		);
	}

	const renderClipBoundTimestamp = (clipBound: Date, className?: string) => {
		if (clipBound < scrollTimelineStart || clipBound > scrollTimelineEnd) {
			return;
		}

		return (
			<Timestamp
				timestamp={ clipBound }
				className={ className }
				includeSeconds
				scrollTimelineEnd={ scrollTimelineEnd }
				scrollTimelineStart={ scrollTimelineStart }
			/>
		);
	};

	const renderClipBoundTimestamps = () => {
		if (!clipModeActive) {
			return;
		}

		return (
			<Fragment>
				{ renderClipBoundTimestamp(new Date(clipEnd), "clip-end-timestamp") }
				{ renderClipBoundTimestamp(new Date(clipStart), "clip-start-timestamp") }
			</Fragment>
		);
	};

	const renderClipArea = () => {
		if (!clipModeActive) {
			return;
		}

		return (
			<ClipArea />
		);
	};

	return (
		<div className="timeline-container">
			<div className="timeline">
				<div
					className="seek-container"
					onWheel={ handleTimelineScroll }
					onMouseMove={ handleTimelineDrag }
					onClick={ handleDragEnd }
				>
					<div className="timestamps">
						{ timestampContent }
						{ renderClipBoundTimestamps() }
					</div>
					{ renderClipArea() }
					<div
						className={ `event-container $timeline-1` }
						onClick={ handleTimelineClick }
					>
						{ renderEvents() }
					</div>
					{ renderPlayhead() }
				</div>
			</div>
		</div>
	);
}

export default memo(HistoricalTimeline);
